import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { Contact, IAPIContact } from 'src/model/contact.model';
import { FileInterface } from 'src/model/file.interface';
import { FileAdapter, FileUrlBuilder } from './file.service';
import { Docuseal } from 'src/model/formJ.model';

@Injectable({
	providedIn: 'root'
})
export class ContactAdapter {
	adapt(item: any): Contact {
		return new Contact(
			item.id,
			item.name,
			item.icNumber,
			item.number,
			item.address,
			item.customerId,
			item.relation,
			item.remark,
			undefined,
			undefined,
			undefined,
			undefined,
			undefined,
			undefined,
			undefined,
			item.dob
		);
	}

	toApiModel(contact: Contact): IAPIContact {
		return {
			...(contact.ID && { id: contact.ID }),
			name: contact.Name,
			icNumber: contact.ICNumber,
			relation: contact.Relation,
			remark: contact.Remark,
			address: contact.Address,
			customerId: contact.CustomerId,
			dob: contact.DOB
		};
	}
}

@Injectable({
	providedIn: 'root'
})
export class ContactService {
	private api = inject(RestApiService);
	private constructApi = inject(ConstructAPI);
	private adapter = inject(ContactAdapter);
	private fileAdapter = inject(FileAdapter);
	private fileUrl = inject(FileUrlBuilder);

	postContact(contact: Contact) {
		return new Promise<Contact>((resolve, reject) => {
			// console.log(this.adapter.toApiModel(customerAccount))
			this.api.post<Contact>(this.constructApi.getAddContact(), this.adapter.toApiModel(contact)).subscribe({
				next: response => {
					resolve(this.adapter.adapt(response));
				},
				error: err => {
					this.api.handleError(err);
					console.error('Failed to add contact: ', err);
					reject(err);
				}
			});
		});
	}

	getAllCustomerContacts(custId: number): Promise<Contact[]> {
		return new Promise<Contact[]>((resolve, reject) => {
			this.api
				.get<Contact[]>(this.constructApi.getCustomerContact(custId))
				.pipe(map(data => data.map(d => this.adapter.adapt(d))))
				.subscribe({
					next: response => resolve(response),
					error: err => {
						this.api.handleError(err);
						console.error('Failed to get all contact: ', err);
						reject(err);
					}
				});
		});
	}

	async getAllCustomerContactsWithPhotoExistence(custId: number): Promise<Contact[]> {
		const contactList: Contact[] = await this.getAllCustomerContacts(custId);
		const promises = contactList.map(c =>
			this.getCustomerContactPhotoExistence(custId, c.ID).then(exist => {
				c.PhotoExisted = exist;
				return c;
			})
		);
		return Promise.all(promises);
	}

	getContactById(customerId: number, contactId: number): Promise<Contact> {
		return new Promise<Contact>((resolve, reject) => {
			this.api.get<Contact>(this.constructApi.getCustomerContactWithContactID(customerId, contactId)).subscribe({
				next: response => resolve(this.adapter.adapt(response)),
				error: err => {
					this.api.handleError(err);
					console.error('Failed to get contact by id: ', err);
					reject(err);
				}
			});
		});
	}

	getContactDetails(contactId: number): Promise<Contact> {
		return new Promise<Contact>((resolve, reject) => {
			this.api.get<Contact>(this.constructApi.getContactDetails(contactId)).subscribe({
				next: response => resolve(this.adapter.adapt(response)),
				error: err => {
					this.api.handleError(err);
					console.error('Failed to get contact details: ', err);
					reject(err);
				}
			});
		});
	}

	getCustomerContactPhotoExistence(customerId: number, contactId: number): Promise<boolean> {
		return new Promise<boolean>((resolve, reject) => {
			this.api.get<boolean>(this.constructApi.getCustomerContactPhotoExistence(customerId, contactId)).subscribe({
				next: response => resolve(Boolean(response)),
				error: err => {
					this.api.handleError(err);
					console.error('Failed to get contact photo existence: ', err);
					reject(err);
				}
			});
		});
	}

	getCustomerContactTypedFileExistence(customerId: number, contactId: number, type: string): Promise<boolean> {
		return new Promise<boolean>((resolve, reject) => {
			this.api.get<boolean>(this.constructApi.getCustomerContactTypedFileExistence(customerId, contactId, type)).subscribe({
				next: response => resolve(Boolean(response)),
				error: err => {
					this.api.handleError(err);
					console.error(`Failed to get contact ${type} existence: ${err}`);
					reject(err);
				}
			});
		});
	}

	getCustomerContactAllFiles(customerId: number, contactId: number): Promise<FileInterface[]> {
		return new Promise<FileInterface[]>((resolve, reject) => {
			this.api
				.get<FileInterface[]>(this.constructApi.getCustomerContactAllFiles(customerId, contactId))
				.pipe(map(data => data.map(d => this.fileAdapter.adapt(d, this.fileUrl.buildCustomerFileUrl(customerId)))))
				.subscribe({
					next: response => resolve(response),
					error: err => {
						this.api.handleError(err);
						console.error('Failed to get all contact files: ', err);
						reject(err);
					}
				});
		});
	}


}
